import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone } from 'lucide-react';

const Footer = () => {
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'EN');

  useEffect(() => {
    const onLangChange = (e) => setLanguage(e.detail || localStorage.getItem('language') || 'EN');
    const onStorage = (e) => { if (e.key === 'language') setLanguage(e.newValue || 'EN'); };
    window.addEventListener('languageChange', onLangChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('languageChange', onLangChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const texts = {
    tagline: { en: 'Pushing the limits of creativity in gaming.', tr: 'Oyunlarda yaratıcılığın sınırlarını zorluyoruz.' },
    getInTouch: { en: 'Get in Touch', tr: 'İletişime Geç' },
    location: { en: 'Poznań, Poland', tr: 'Poznań, Polonya' },
    copyright: { en: '© 2026 E&B Games. All rights reserved.', tr: '© 2026 E&B Games. Tüm hakları saklıdır.' },
    privacy: { en: 'Privacy Policy', tr: 'Gizlilik Politikası' },
    terms:   { en: 'Terms of Service', tr: 'Hizmet Şartları' },
    cookie:  { en: 'Cookie Policy', tr: 'Çerez Politikası' },
  };
  const t = (key) => (language === 'TR' ? texts[key].tr : texts[key].en);

  const footerLinksEN = {
    'Games': [
      { name: 'All Games', path: '/games' },
      { name: 'New Releases', path: '/games' },
      { name: 'Coming Soon', path: '/coming' },
      { name: 'Early Access', path: '/coming' }
    ],
    'Company': [
      { name: 'About Us', path: '/about' },
      { name: 'News', path: '/news' },
      { name: 'Careers', path: '/contact' }
    ],
    'Support': [
      { name: 'Contact', path: '/contact' },
      { name: 'Help Center', path: '/contact' },
      { name: 'Bug Reports', path: '/contact' },
      { name: 'Community', path: '/contact' }
    ]
  };

  const footerLinksTR = {
    'Oyunlar': [
      { name: 'Tüm Oyunlar', path: '/games' },
      { name: 'Yeni Çıkanlar', path: '/games' },
      { name: 'Yakında', path: '/coming' },
      { name: 'Erken Erişim', path: '/coming' }
    ],
    'Şirket': [
      { name: 'Hakkımızda', path: '/about' },
      { name: 'Haberler', path: '/news' },
      { name: 'Kariyer', path: '/contact' }
    ],
    'Destek': [
      { name: 'İletişim', path: '/contact' },
      { name: 'Yardım Merkezi', path: '/contact' },
      { name: 'Hata Bildirimi', path: '/contact' },
      { name: 'Topluluk', path: '/contact' }
    ]
  };

// Aktif dilin link setini seç
  const linksData = language === 'TR' ? footerLinksTR : footerLinksEN;

  return (
    <footer className="bg-gaming-darker border-t border-gaming-primary/20">

      {/* Main Footer */}
      <div className="py-12 sm:py-14 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 sm:gap-8">
            {/* Brand Section */}
            <div className="lg:col-span-2">
                <Link to="/" className="flex items-center gap-3 mb-5 sm:mb-6 flex-wrap">
                  <motion.img
                    src="/assets/images/eblogo.png"
                    alt="E&B Games official logo"
                    className="h-10 sm:h-12 w-auto rounded-lg select-none shrink-0"
                    whileHover={{ scale: 1.1, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                    draggable={false}
                  />
                  <span className="font-gaming text-xl sm:text-2xl font-bold text-gaming-primary leading-tight break-words">
                    E&B Games
                  </span>
                </Link>

              <p className="text-gray-400 font-display text-sm sm:text-base leading-relaxed mb-6 max-w-md">
                {t('tagline')}
              </p>

              {/* Social Media Icons */}
            </div>

            {/* Footer Links */}
            {Object.entries(linksData).map(([category, links]) => (
              <div key={category}>
                <h3 className="font-gaming font-bold text-base sm:text-lg text-gaming-primary mb-4 sm:mb-6">
                  {category}
                </h3>
                <ul className="space-y-2.5 sm:space-y-3">
                  {links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.path}
                        className="text-gray-400 hover:text-gaming-primary transition-colors duration-300 font-display"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Contact Info */}
            <div>
              <h3 className="font-gaming font-bold text-base sm:text-lg text-gaming-primary mb-4 sm:mb-6">
                {t('getInTouch')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="text-gaming-secondary mt-0.5 shrink-0" size={20} />
                  <a
                    href="mailto:eandbgamestudio@gmail.com"
                    className="text-gray-400 hover:text-gaming-primary transition-colors font-display break-words"
                  >
                    eandbgamestudio@gmail.com
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="text-gaming-secondary mt-0.5 shrink-0" size={20} />
                  <span className="text-gray-400 font-display break-words">+48 (573) 996 078</span>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="text-gaming-secondary mt-0.5 shrink-0" size={20} />
                  <span className="text-gray-400 font-display break-words">{t('location')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gaming-primary/20 py-5 sm:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center md:items-center gap-4 text-center md:text-left">
            <div className="text-gray-400 text-sm font-display leading-relaxed">
              {t('copyright')}
            </div>

            <div className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-2 text-sm">
              <Link
                to="/privacy-policy"
                className="text-gray-400 hover:text-gaming-primary transition-colors font-display whitespace-nowrap"
              >
                {t('privacy')}
              </Link>
              <Link
                to="/terms-of-service"
                className="text-gray-400 hover:text-gaming-primary transition-colors font-display whitespace-nowrap"
              >
                {t('terms')}
              </Link>
              <Link
                to="/cookie-policy"
                className="text-gray-400 hover:text-gaming-primary transition-colors font-display whitespace-nowrap"
              >
                {t('cookie')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;